import { useAppTheme } from "../../useAppTheme";
import { useDevice } from "../../useDevice";
import Box from "../Box";
import { Skeleton } from "../Skeleton";
import { TipCallout } from "../TipCallout";

const TILE_GAP = 2;
const HEADLINE_LINE_HEIGHT = 36;
const BODY_LINE_HEIGHT = 20;
const LABEL_LINE_HEIGHT = 16;
const TITLE_SMALL_LINE_HEIGHT = 20;
const STAT_ICON_SIZE = 24;

type Props = {
	numStats?: number;
	showTipPlaceholder?: boolean;
	showSubtitle?: boolean;
};

const TextLine = ({
	lineHeight,
	barHeight,
	width,
}: {
	lineHeight: number;
	barHeight: number;
	width: number | `${number}%`;
}) => (
	<Box height={lineHeight} justifyContent="center" alignItems="center">
		<Skeleton width={width} height={barHeight} />
	</Box>
);

const StatTileCellSkeleton = () => {
	const theme = useAppTheme();
	return (
		<Box
			flex={1}
			minWidth={0}
			backgroundColor={theme.colors.surface}
			alignItems="center"
			justifyContent="center"
			gap={4}
			paddingVertical={8}
			paddingHorizontal={12}
		>
			<Skeleton size={STAT_ICON_SIZE} variant="circle" />
			<TextLine lineHeight={LABEL_LINE_HEIGHT} barHeight={12} width={56} />
			<TextLine
				lineHeight={TITLE_SMALL_LINE_HEIGHT}
				barHeight={14}
				width={40}
			/>
		</Box>
	);
};

export const ProgressOverviewSkeleton = ({
	numStats = 4,
	showTipPlaceholder = true,
	showSubtitle = true,
}: Props) => {
	const theme = useAppTheme();
	const { isMobile } = useDevice();

	const maxColumns = isMobile ? 2 : 4;
	const rows = Math.ceil(numStats / maxColumns);
	const columns = Math.max(1, Math.ceil(numStats / rows));
	const statRows: number[][] = [];
	for (let index = 0; index < numStats; index += columns) {
		statRows.push(
			Array.from(
				{ length: Math.min(columns, numStats - index) },
				(_, i) => index + i,
			),
		);
	}

	return (
		<Box gap={TILE_GAP} borderRadius={theme.roundness * 3} overflow="hidden">
			<Box
				backgroundColor={theme.colors.surface}
				alignItems="center"
				gap={12}
				paddingVertical={24}
				paddingHorizontal={16}
			>
				<TextLine
					lineHeight={HEADLINE_LINE_HEIGHT}
					barHeight={24}
					width={220}
				/>
				{showSubtitle ? (
					<TextLine lineHeight={BODY_LINE_HEIGHT} barHeight={14} width={140} />
				) : null}
			</Box>
			{statRows.map((row) => (
				<Box key={row.join("-")} flexDirection="row" gap={TILE_GAP}>
					{row.map((cellIndex) => (
						<StatTileCellSkeleton key={cellIndex} />
					))}
				</Box>
			))}
			{showTipPlaceholder ? (
				<Box backgroundColor={theme.colors.surface} padding={16}>
					<TipCallout isLoading />
				</Box>
			) : null}
		</Box>
	);
};
